import React, { useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { ArrowLeft, FileText } from 'lucide-react';
import { Skeleton } from '../../../shared/components/Skeleton';
import { Alert } from '../../../shared/components/Alert';
import { Button } from '../../../shared/components/Button';
import { usePermissions } from '../../../shared/hooks/usePermissions';
import { FEATURES } from '../../../shared/lib/permissions';
import { openAuthenticatedFile } from '../../../shared/lib/files';
import { authService } from '../../../shared/lib/auth';
import { buscarCliente } from '../../clientes/api';
import { listarUsuarios } from '../../usuarios/api';
import {
  useAssinarComoSignatarioInterno,
  useAtualizarSituacao,
  useContrato,
  useCriarAditivo,
  useEnviarParaAssinatura,
  useHistoricoContrato,
  useReenviarConviteAssinatura,
  useRegistrarAssinaturaManual,
  useRenovarContrato,
  useSaldoContrato,
  useStatusAssinatura,
} from '../hooks';
import type { ContractSituacao, SignatoryInput } from '../api';
import { formatCurrencyBRL, formatDateBR } from '../utils/format';
import { ContractOriginPanel } from '../components/ContractOriginPanel';
import { extractCreationContext } from '../api';
import { ContractClausesSection } from '../components/ContractClausesSection';
import { ContractAttachmentsSection } from '../components/ContractAttachmentsSection';
import { ContractAddendaSection } from '../components/ContractAddendaSection';
import { ContractAlertConfigSection } from '../components/ContractAlertConfigSection';
import {
  CancelContractModal,
  CloseContractModal,
  ConfirmTransitionModal,
  CreateAddendumModal,
  RegisterManualSignatureModal,
  RenewContractModal,
  RescindContractModal,
  SendForSignatureModal,
  SuspendContractModal,
} from '../components/ActionModals';

type ActiveModal =
  | 'suspend' | 'rescind' | 'close' | 'renew' | 'addendum'
  | 'submitReview' | 'submitApproval' | 'returnToReview' | 'approve' | 'activate' | 'cancel'
  | 'sendSignature' | 'registerSignature'
  | null;

const SITUACAO_LABEL: Record<ContractSituacao, string> = {
  em_elaboracao: 'Em Elaboração',
  em_revisao_juridica: 'Em Revisão Jurídica',
  em_aprovacao: 'Em Aprovação',
  aguardando_assinatura: 'Aguardando Assinatura',
  assinado: 'Assinado',
  vigente: 'Vigente',
  suspenso: 'Suspenso',
  em_renovacao: 'Em Renovação',
  em_aditivo: 'Em Aditivo',
  encerrado: 'Encerrado',
  rescindido: 'Rescindido',
  cancelado: 'Cancelado',
};

const SITUACAO_COLOR: Record<ContractSituacao, string> = {
  vigente: 'bg-green-100 text-green-800',
  suspenso: 'bg-yellow-100 text-yellow-800',
  em_aditivo: 'bg-yellow-100 text-yellow-800',
  encerrado: 'bg-red-100 text-red-800',
  rescindido: 'bg-red-100 text-red-800',
  cancelado: 'bg-red-100 text-red-800',
  em_elaboracao: 'bg-blue-100 text-blue-800',
  em_revisao_juridica: 'bg-blue-100 text-blue-800',
  em_aprovacao: 'bg-blue-100 text-blue-800',
  aguardando_assinatura: 'bg-blue-100 text-blue-800',
  assinado: 'bg-blue-100 text-blue-800',
  em_renovacao: 'bg-blue-100 text-blue-800',
};

const SIGNATORY_STATUS_LABEL: Record<string, string> = {
  pending: 'Pendente',
  signed: 'Assinado',
  rejected: 'Rejeitado',
};

const DIMENSION_LABEL: Record<string, string> = {
  financeiro: 'Financeiro',
  horas: 'Horas',
  recursos: 'Recursos',
  consumo: 'Consumo',
};

export default function ContratoDetalhePage(): React.ReactElement {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [activeModal, setActiveModal] = useState<ActiveModal>(null);
  const [isPreviewLoading, setIsPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState('');
  const { can } = usePermissions();

  const { data: contract, isLoading, isError, error } = useContrato(id);
  const { data: balances } = useSaldoContrato(id);
  const { data: historyData } = useHistoricoContrato(id);
  const { mutate: atualizarSituacao, isPending: isSituacaoLoading } = useAtualizarSituacao();
  const { data: signature } = useStatusAssinatura(id);
  const { mutate: enviarAssinatura, isPending: isSendingSignature } = useEnviarParaAssinatura();
  const { mutate: registrarAssinatura, isPending: isRegisteringSignature } = useRegistrarAssinaturaManual();
  const { mutate: assinarInterno, isPending: isSigningInternal } = useAssinarComoSignatarioInterno();
  const { mutate: renovarContrato, isPending: isRenewing } = useRenovarContrato();
  const { mutate: criarAditivo, isPending: isCreatingAddendum } = useCriarAditivo();
  const { mutate: reenviarConvite, isPending: isResendingInvite, variables: resendingVars } = useReenviarConviteAssinatura();
  const currentUserId = authService.getCurrentUser()?.id;

  const { data: clienteResp } = useQuery({
    queryKey: ['picker', 'cliente', contract?.clientId],
    queryFn: () => buscarCliente(contract!.clientId),
    enabled: !!contract?.clientId,
  });
  const { data: usuariosResp } = useQuery({
    queryKey: ['picker', 'usuarios'],
    queryFn: () => listarUsuarios({ limit: 200 }),
    staleTime: 60 * 1000,
  });
  const usuarioNome = useMemo(() => {
    const map = new Map((usuariosResp?.data ?? []).map((u) => [u.id, u.name]));
    return (userId: string) => map.get(userId) ?? userId;
  }, [usuariosResp]);

  if (isLoading) {
    return (
      <div className="p-6 space-y-4">
        <Skeleton className="h-8 w-64" />
        <Skeleton className="h-32 w-full" />
      </div>
    );
  }

  if (isError || !contract) {
    return (
      <div className="p-6">
        <Alert variant="error">{(error as Error)?.message ?? 'Contrato não encontrado.'}</Alert>
      </div>
    );
  }

  const sit = contract.situacao;

  // Ciclo de vida pós-formalização — ações já existentes.
  const canSuspend = sit === 'vigente';
  const canReactivate = sit === 'suspenso';
  const canClose = sit === 'vigente' || sit === 'suspenso' || sit === 'em_renovacao';
  const canRescind = sit === 'vigente' || sit === 'suspenso' || sit === 'em_renovacao';
  const canRenew = sit === 'vigente';

  // Ciclo de vida inicial — elaboração → revisão → aprovação → assinatura.
  const canEditContrato = can(FEATURES.contratos, 'edit');
  const canApproveContrato = can(FEATURES.contratosAprovacao, 'edit');
  const canSignContrato = can(FEATURES.contratosAssinatura, 'create');
  const canCreateAddendum = can(FEATURES.contratosAditivos, 'create') && ['vigente', 'suspenso'].includes(sit);
  const canEditAddendum = can(FEATURES.contratosAditivos, 'edit');

  const canSubmitReview = canEditContrato && sit === 'em_elaboracao';
  const canSubmitApproval = canEditContrato && (sit === 'em_elaboracao' || sit === 'em_revisao_juridica');
  const canReturnToReview = canEditContrato && sit === 'em_aprovacao';
  const canApprove = canApproveContrato && sit === 'em_aprovacao';
  const canActivate = canEditContrato && sit === 'assinado';
  const canCancel = canEditContrato
    && ['em_elaboracao', 'em_revisao_juridica', 'em_aprovacao', 'aguardando_assinatura'].includes(sit);

  const showSignaturePanel = ['aguardando_assinatura', 'assinado', 'vigente', 'em_renovacao', 'suspenso', 'em_aditivo'].includes(sit);
  const canManageSignature = canSignContrato && sit === 'aguardando_assinatura'
    && (!signature || signature.status === 'rejected' || signature.status === 'cancelled');
  // Signatário interno pendente: o próprio usuário logado está na lista e ainda não respondeu.
  const myPendingSignature = signature?.status === 'in_progress'
    ? signature.signatories.find((s) => s.userId === currentUserId && s.status === 'pending')
    : undefined;

  const doReactivate = () => {
    if (!id) return;
    atualizarSituacao({ id, situacao: 'vigente', reason: 'Reativação via painel' });
  };

  const doPreviewPdf = async () => {
    if (!id) return;
    setIsPreviewLoading(true);
    setPreviewError('');
    try {
      await openAuthenticatedFile(`/api/contracts/${id}/preview-pdf`);
    } catch {
      setPreviewError('Não foi possível gerar o PDF de pré-visualização.');
    } finally {
      setIsPreviewLoading(false);
    }
  };

  const doSimpleTransition = (situacao: ContractSituacao) => (reason?: string) => {
    if (!id) return;
    atualizarSituacao({ id, situacao, reason }, { onSuccess: () => setActiveModal(null) });
  };

  const historyEntries = historyData?.data ?? [];
  const creationContext = extractCreationContext(historyEntries);

  return (
    <div className="p-4 sm:p-6 max-w-5xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex items-start gap-4">
        <button onClick={() => navigate('/contratos')} aria-label="Voltar" className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100">
          <ArrowLeft className="w-5 h-5" />
        </button>
        <div className="flex-1">
          <div className="flex items-center gap-3 flex-wrap">
            <span className="font-mono text-sm text-gray-500">{contract.codigo ?? '—'}</span>
            <h1 className="text-xl font-semibold text-gray-900">{contract.numero}</h1>
            <span className={`inline-flex whitespace-nowrap items-center rounded-full px-2 py-0.5 text-xs font-medium ${SITUACAO_COLOR[sit]}`}>
              {SITUACAO_LABEL[sit]}
            </span>
          </div>
          <div className="flex gap-2 mt-3 flex-wrap">
            <Button
              variant="secondary"
              size="sm"
              loading={isPreviewLoading}
              icon={<FileText className="w-3.5 h-3.5" />}
              onClick={doPreviewPdf}
            >
              Visualizar PDF
            </Button>
            {canSubmitReview && (
              <Button size="sm" onClick={() => setActiveModal('submitReview')}>Enviar para Revisão Jurídica</Button>
            )}
            {canSubmitApproval && (
              <Button size="sm" onClick={() => setActiveModal('submitApproval')}>Enviar para Aprovação</Button>
            )}
            {canReturnToReview && (
              <Button size="sm" variant="secondary" onClick={() => setActiveModal('returnToReview')}>Devolver para Revisão Jurídica</Button>
            )}
            {canApprove && (
              <Button size="sm" onClick={() => setActiveModal('approve')}>Aprovar</Button>
            )}
            {canActivate && (
              <Button size="sm" onClick={() => setActiveModal('activate')}>Ativar Contrato</Button>
            )}
            {canSuspend && (
              <Button size="sm" variant="secondary" onClick={() => setActiveModal('suspend')}>Suspender</Button>
            )}
            {canReactivate && (
              <Button size="sm" loading={isSituacaoLoading} onClick={doReactivate}>Reativar</Button>
            )}
            {canClose && (
              <Button size="sm" variant="secondary" onClick={() => setActiveModal('close')}>Encerrar</Button>
            )}
            {canRescind && (
              <Button size="sm" variant="danger" onClick={() => setActiveModal('rescind')}>Rescindir</Button>
            )}
            {canRenew && (
              <Button size="sm" onClick={() => setActiveModal('renew')}>Renovar</Button>
            )}
            {canCancel && (
              <Button size="sm" variant="danger" onClick={() => setActiveModal('cancel')}>Cancelar Contrato</Button>
            )}
          </div>
          {previewError && <Alert variant="error" className="mt-2">{previewError}</Alert>}
        </div>
      </div>

      {/* Informações */}
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <h2 className="text-base font-semibold text-gray-900 mb-4">Informações</h2>
        <dl className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 text-sm">
          <div><dt className="text-gray-500">Cliente</dt><dd className="mt-0.5 text-gray-700">{clienteResp?.razaoSocial ?? contract.clientId}</dd></div>
          <div><dt className="text-gray-500">Responsável Comercial</dt><dd className="mt-0.5 text-gray-700">{usuarioNome(contract.responsavelComercialId)}</dd></div>
          <div><dt className="text-gray-500">Gestor do Contrato</dt><dd className="mt-0.5 text-gray-700">{usuarioNome(contract.gestorContratoId)}</dd></div>
          <div><dt className="text-gray-500">Vigência Inicial</dt><dd className="mt-0.5 text-gray-700">{formatDateBR(contract.vigenciaInicial)}</dd></div>
          <div><dt className="text-gray-500">Vigência Final</dt><dd className="mt-0.5 text-gray-700">{formatDateBR(contract.vigenciaFinal)}</dd></div>
          <div><dt className="text-gray-500">Valor Total</dt><dd className="mt-0.5 text-gray-700">{formatCurrencyBRL(contract.valorTotal)} {contract.moeda}</dd></div>
          <div><dt className="text-gray-500">Recorrente</dt><dd className="mt-0.5 text-gray-700">{contract.isRecorrente ? 'Sim' : 'Não'}</dd></div>
          {contract.objeto && <div className="sm:col-span-2"><dt className="text-gray-500">Objeto</dt><dd className="mt-0.5 text-gray-700">{contract.objeto}</dd></div>}
          {contract.observacoes && <div className="sm:col-span-2"><dt className="text-gray-500">Observações</dt><dd className="mt-0.5 text-gray-700">{contract.observacoes}</dd></div>}
        </dl>
      </div>

      <ContractClausesSection contractId={id as string} modality={contract.modalidade} canEdit={canEditContrato} />

      <ContractAttachmentsSection contractId={id as string} canEdit={canEditContrato} />

      {/* Assinatura */}
      {showSignaturePanel && (
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-base font-semibold text-gray-900">Assinatura</h2>
            {canManageSignature && (
              <div className="flex gap-2">
                <Button size="sm" onClick={() => setActiveModal('sendSignature')}>Enviar para Assinatura Eletrônica</Button>
                <Button size="sm" variant="secondary" onClick={() => setActiveModal('registerSignature')}>Registrar Assinatura Presencial</Button>
              </div>
            )}
          </div>
          {myPendingSignature && (
            <div className="mb-4 flex items-center justify-between rounded-lg bg-blue-50 border border-blue-200 p-3">
              <p className="text-sm text-blue-900">Você é signatário deste contrato e ainda não assinou.</p>
              <Button size="sm" loading={isSigningInternal} onClick={() => id && assinarInterno(id)}>Assinar</Button>
            </div>
          )}
          {!signature ? (
            <p className="text-sm text-gray-500">Nenhum processo de assinatura iniciado.</p>
          ) : (
            <div className="space-y-3">
              <p className="text-sm text-gray-600">
                Provedor: <span className="font-medium">{signature.provider}</span> • Status: <span className="font-medium">{signature.status}</span>
              </p>
              <ul className="divide-y divide-gray-100 text-sm">
                {signature.signatories.map((s) => (
                  <li key={s.email} className="py-2 flex items-center justify-between gap-2">
                    <div>
                      <p className="font-medium text-gray-800">{s.name}</p>
                      <p className="text-xs text-gray-500">{s.email}</p>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      {canSignContrato && !s.userId && s.status === 'pending' && (
                        <button
                          disabled={isResendingInvite && resendingVars?.email === s.email}
                          onClick={() => id && reenviarConvite({ id, email: s.email })}
                          className="text-xs text-blue-600 hover:text-blue-800 disabled:opacity-60"
                        >
                          {isResendingInvite && resendingVars?.email === s.email ? 'Reenviando…' : 'Reenviar convite'}
                        </button>
                      )}
                      <span className={`text-xs px-2 py-0.5 rounded-full ${s.status === 'signed' ? 'bg-green-100 text-green-800' : s.status === 'rejected' ? 'bg-red-100 text-red-700' : 'bg-gray-100 text-gray-600'}`}>
                        {SIGNATORY_STATUS_LABEL[s.status] ?? s.status}
                      </span>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}

      <ContractAddendaSection
        contractId={id as string}
        canCreate={canCreateAddendum}
        canEdit={canEditAddendum}
        onCreateClick={() => setActiveModal('addendum')}
      />

      <ContractAlertConfigSection contractId={id as string} canEdit={canEditContrato} />

      {/* Saldo Contratual */}
      {balances && balances.length > 0 && (
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <h2 className="text-base font-semibold text-gray-900 mb-4">Saldo Contratual</h2>
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left font-medium text-gray-500">Dimensão</th>
                  <th className="px-4 py-3 text-right font-medium text-gray-500">Saldo Inicial</th>
                  <th className="px-4 py-3 text-right font-medium text-gray-500">Saldo Atual</th>
                  <th className="px-4 py-3 text-left font-medium text-gray-500">Unidade</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {balances.map((b) => (
                  <tr key={b.id}>
                    <td className="px-4 py-3 font-medium text-gray-900">{DIMENSION_LABEL[b.dimension] ?? b.dimension}</td>
                    <td className="px-4 py-3 text-right text-gray-600">{b.dimension === 'financeiro' ? formatCurrencyBRL(b.initialBalance) : b.initialBalance}</td>
                    <td className="px-4 py-3 text-right text-gray-900 font-medium">{b.dimension === 'financeiro' ? formatCurrencyBRL(b.currentBalance) : b.currentBalance}</td>
                    <td className="px-4 py-3 text-gray-500">{b.unit}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Origem (RN-COM-003) */}
      <ContractOriginPanel context={creationContext} />

      {/* Histórico */}
      {historyEntries.length > 0 && (
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <h2 className="text-base font-semibold text-gray-900 mb-4">Histórico de Alterações</h2>
          <ul className="space-y-3">
            {historyEntries.slice(0, 10).map((entry) => (
              <li key={entry.id} className="flex items-start gap-3 text-sm">
                <div className="w-2 h-2 rounded-full bg-blue-400 mt-1.5 shrink-0" />
                <div>
                  <span className="font-medium text-gray-800">{entry.action}</span>
                  {entry.reason && <span className="text-gray-500"> — {entry.reason}</span>}
                  <p className="text-xs text-gray-400 mt-0.5">{formatDateBR(entry.createdAt)}</p>
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Modals — ciclo de vida inicial */}
      <ConfirmTransitionModal
        isOpen={activeModal === 'submitReview'}
        onClose={() => setActiveModal(null)}
        onConfirm={doSimpleTransition('em_revisao_juridica')}
        isSubmitting={isSituacaoLoading}
        title="Enviar para Revisão Jurídica"
        description="O contrato será encaminhado para revisão jurídica antes de seguir para aprovação."
        confirmLabel="Enviar"
      />
      <ConfirmTransitionModal
        isOpen={activeModal === 'submitApproval'}
        onClose={() => setActiveModal(null)}
        onConfirm={doSimpleTransition('em_aprovacao')}
        isSubmitting={isSituacaoLoading}
        title="Enviar para Aprovação"
        description="O contrato será submetido ao(s) aprovador(es) responsáveis (perfil com permissão de Aprovar Contratos)."
        confirmLabel="Enviar"
      />
      <ConfirmTransitionModal
        isOpen={activeModal === 'returnToReview'}
        onClose={() => setActiveModal(null)}
        onConfirm={doSimpleTransition('em_revisao_juridica')}
        isSubmitting={isSituacaoLoading}
        title="Devolver para Revisão Jurídica"
        description="O contrato voltará para a etapa de revisão jurídica."
        confirmLabel="Devolver"
        requireReason
      />
      <ConfirmTransitionModal
        isOpen={activeModal === 'approve'}
        onClose={() => setActiveModal(null)}
        onConfirm={doSimpleTransition('aguardando_assinatura')}
        isSubmitting={isSituacaoLoading}
        title="Aprovar Contrato"
        description="O contrato ficará pronto para ser encaminhado para assinatura."
        confirmLabel="Aprovar"
      />
      <ConfirmTransitionModal
        isOpen={activeModal === 'activate'}
        onClose={() => setActiveModal(null)}
        onConfirm={doSimpleTransition('vigente')}
        isSubmitting={isSituacaoLoading}
        title="Ativar Contrato"
        description="O contrato assinado passará à situação Vigente, liberando a criação de Ordens de Serviço."
        confirmLabel="Ativar"
      />
      <CancelContractModal
        isOpen={activeModal === 'cancel'}
        onClose={() => setActiveModal(null)}
        onConfirm={doSimpleTransition('cancelado')}
        isSubmitting={isSituacaoLoading}
      />

      {/* Modals — assinatura */}
      <SendForSignatureModal
        isOpen={activeModal === 'sendSignature'}
        onClose={() => setActiveModal(null)}
        onConfirm={(signatories: SignatoryInput[]) => id && enviarAssinatura({ id, signatories }, { onSuccess: () => setActiveModal(null) })}
        isSubmitting={isSendingSignature}
      />
      <RegisterManualSignatureModal
        isOpen={activeModal === 'registerSignature'}
        onClose={() => setActiveModal(null)}
        onConfirm={(signatories: SignatoryInput[]) => id && registrarAssinatura({ id, signatories }, { onSuccess: () => setActiveModal(null) })}
        isSubmitting={isRegisteringSignature}
      />

      {/* Modals — ciclo de vida pós-formalização */}
      <SuspendContractModal
        isOpen={activeModal === 'suspend'}
        onClose={() => setActiveModal(null)}
        onConfirm={(d) => id && atualizarSituacao({ id, situacao: 'suspenso', reason: d.reason }, { onSuccess: () => setActiveModal(null) })}
        isSubmitting={isSituacaoLoading}
      />
      <RescindContractModal
        isOpen={activeModal === 'rescind'}
        onClose={() => setActiveModal(null)}
        onConfirm={(d) => id && atualizarSituacao({ id, situacao: 'rescindido', reason: d.motivo }, { onSuccess: () => setActiveModal(null) })}
        isSubmitting={isSituacaoLoading}
      />
      <CloseContractModal
        isOpen={activeModal === 'close'}
        onClose={() => setActiveModal(null)}
        onConfirm={(d) => id && atualizarSituacao({ id, situacao: 'encerrado', reason: d.reason }, { onSuccess: () => setActiveModal(null) })}
        isSubmitting={isSituacaoLoading}
      />
      <RenewContractModal
        isOpen={activeModal === 'renew'}
        onClose={() => setActiveModal(null)}
        onConfirm={(d) => id && renovarContrato({ id, payload: d }, { onSuccess: () => setActiveModal(null) })}
        isSubmitting={isRenewing}
        vigenciaFinalAtual={contract.vigenciaFinal}
      />
      <CreateAddendumModal
        isOpen={activeModal === 'addendum'}
        onClose={() => setActiveModal(null)}
        onConfirm={(d) => id && criarAditivo({ id, payload: d }, { onSuccess: () => setActiveModal(null) })}
        isSubmitting={isCreatingAddendum}
      />
    </div>
  );
}
